// NATIVE 
import React, { useEffect, useState } from 'react';

// INSTALLED 
import { useDispatch, useSelector } from 'react-redux';
import VerifiedIcon from '@mui/icons-material/Verified';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { Accordion, AccordionDetails, AccordionSummary, Box, Button, Card, Checkbox, FormControlLabel, FormGroup, Grid, Tabs, Typography } from '@mui/material';

// CUSTOM 
import { eachWordUpper } from '../../../../commons/utils';
import { paddingCardsTables } from '../../../../commons';
import { SinDatos } from 'commons/generics/SinDatos';
import CustomButtonTab from '../../../../components/CustomButtonTab';
import { Apps } from '@mui/icons-material';
import { getApplicationsPermissionsCustomerStore, updatePermissionsCustomer } from '../../../../store/customer';
import { useResponseHandler } from '../../../../hooks/useResponseHandler';

function a11yProps(index: number) {
    return {
      id: `simple-tab-${index}`,
      'aria-controls': `simple-tabpanel-${index}`,
    };
}

const RenderChildren = ({ 
  element, 
  parentSlugs, 
  isParentEnabled,
  selectedModules,
  selectedActions,
  handleToggleModule,
  handleToggleAction
}: any) => {
  const key = element.slug || element.permission_slug;
  const isModuleEnabled = !!selectedModules[key];
  const isCheckboxDisabled = !isParentEnabled;
  const isActionDisabled = isCheckboxDisabled || !isModuleEnabled;

  const myParentSlugs = [...parentSlugs, key];
  
  // Lista de acciones disponibles en el sistema
  const defaultActions = ['view', 'create', 'edit', 'delete', 'restore'];

  return (
    <Accordion sx={{ mt: 1, border: '1px solid #e0e0e0', boxShadow: 'none' }}>
      <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ backgroundColor: '#fafafa' }}>
        <Box sx={{ display: 'flex', flexDirection: 'column' }}>
          <Typography variant="subtitle1" fontWeight="bold">
            {eachWordUpper(element.text)}
          </Typography>
          {element.subText && (
            <Typography variant="body2" color="text.secondary">
              {eachWordUpper(element.subText)}
            </Typography>
          )}
        </Box>
      </AccordionSummary>
      <AccordionDetails sx={{ backgroundColor: '#fff', padding: 2 }}>
        <Box sx={{ display: 'flex', flexDirection: 'column' }}>
          
          <Box sx={{ pb: 1, borderBottom: '1px solid #e0e0e0' }}>
            <FormGroup>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={isModuleEnabled}
                    disabled={isCheckboxDisabled}
                    onChange={(e) => handleToggleModule(key, e.target.checked, parentSlugs)}
                    name={`mod_${key}`}
                  />
                }
                label="Módulo habilitado"
              />
            </FormGroup>
          </Box>

          {(!element.childrens || element.childrens.length === 0) && (
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, pt: 2, pb: 2 }}>
              {defaultActions.map((action) => {
                const actionEnabled = (selectedActions[key] || []).includes(action);
                return (
                  <FormGroup key={action}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={actionEnabled}
                          disabled={isActionDisabled}
                          onChange={(e) => handleToggleAction(key, action, e.target.checked)}
                          name={`${action}_${key}`}
                        />
                      }
                      label={
                        action === 'view' ? 'Ver' :
                        action === 'create' ? 'Crear' :
                        action === 'edit' ? 'Editar' :
                        action === 'delete' ? 'Eliminar' : 'Restaurar'
                      }
                    />
                  </FormGroup>
                );
              })}
            </Box>
          )}

          {element.childrens && element.childrens.length > 0 && (
            <Box sx={{ mt: 1, ml: 2, borderLeft: '2px solid #f0f0f0', pl: 2 }}>
              {element.childrens.map((child: any, idx: number) => (
                <RenderChildren
                  key={idx}
                  element={child}
                  parentSlugs={myParentSlugs}
                  isParentEnabled={isModuleEnabled}
                  selectedModules={selectedModules}
                  selectedActions={selectedActions}
                  handleToggleModule={handleToggleModule}
                  handleToggleAction={handleToggleAction}
                />
              ))}
            </Box>
          )}
        </Box>
      </AccordionDetails>
    </Accordion>
  );
};

export const EditarModuloPermisosCustomer = () => {

  const dispatch = useDispatch();
  const { handleResponse, ResponseComponents } = useResponseHandler();

  // REDUX 
  const { customer }: any = useSelector((state: any) => state.customer);
  const { permissionsTree }: any = useSelector((state: any) => state.permissions);

  // USE STATE 
  const [value, setValue] = useState(0);
  const [applicationUpdateId, setApplicationUpdate] = useState<any>('');

  // N-LEVEL DYNAMIC STATE (O(1) lookups)
  const [selectedModules, setSelectedModules] = useState<Record<string, boolean>>({});
  const [selectedActions, setSelectedActions] = useState<Record<string, string[]>>({});
  
  // Visual state for Top-Level checkboxes to start unchecked
  const [topLevelChecks, setTopLevelChecks] = useState<Record<string, boolean>>({});

  useEffect(() => {
    if (customer.applications && customer.applications.length > 0) {
      const firstApp = customer.applications[0];
      setApplicationUpdate(firstApp.id);
      dispatch(getApplicationsPermissionsCustomerStore(firstApp.idCustomerApplication, customer.id));
    }
  }, []);

  // Initialize selected modules and actions from the permissionsTree dynamically
  useEffect(() => {
    if (!permissionsTree || permissionsTree.length === 0) return;

    const initModules: Record<string, boolean> = {};
    const initActions: Record<string, string[]> = {};

    const extract = (nodes: any[]) => {
      if (!nodes) return;
      for (const node of nodes) {
        if (node.isChecked) {
          const key = node.slug || node.permission_slug;
          initModules[key] = true;
          initActions[key] = node.actions || [];
        }
        if (node.childrens && node.childrens.length > 0) {
          extract(node.childrens);
        }
      }
    };

    extract(permissionsTree);
    setSelectedModules(initModules);
    setSelectedActions(initActions);
  }, [permissionsTree]);

  const handleChangeApps = (event: React.SyntheticEvent, newValue: number) => {
      // Manejado en handleChangePermissions
  };

  const handleChangePermissions = async (newValue: any, app: any) => {
    setApplicationUpdate(app.id);
    await dispatch(getApplicationsPermissionsCustomerStore(app.idCustomerApplication, customer.id));
    setValue(newValue);
  };

  const updatePermissions = async () => {
    // Reconstruir recursivamente el payload descartando los nodos que ya no están marcados
    // Esto evita guardar "basura" en el backend si un módulo está desmarcado
    const buildPayload = (nodes: any[]): any[] => {
      if (!nodes) return [];
      
      const result: any[] = [];
      for (const node of nodes) {
        const key = node.slug || node.permission_slug;
        if (selectedModules[key]) {
          const newNode = { ...node };
          newNode.isChecked = true;
          newNode.actions = selectedActions[key] || [];
          newNode.childrens = buildPayload(node.childrens);
          result.push(newNode);
        }
      }
      return result;
    };

    const payload = buildPayload(permissionsTree);
    const response = await dispatch(updatePermissionsCustomer(customer.id, applicationUpdateId, payload));
    await handleResponse(response, 'Permisos actualizados', true);
    if (response.ok) {
      await dispatch(getApplicationsPermissionsCustomerStore(applicationUpdateId, customer.id));
    }
  };

  const handleToggleModule = (slug: string, isChecked: boolean, parentSlugs: string[]) => {
    setSelectedModules((prev) => {
      const next = { ...prev };
      next[slug] = isChecked;
      if (isChecked) {
        // Al habilitar un módulo, auto-habilitar recursivamente todos sus padres
        parentSlugs.forEach((pSlug) => {
          next[pSlug] = true;
        });
      }
      return next;
    });
  };

  const handleToggleAction = (slug: string, action: string, isChecked: boolean) => {
    setSelectedActions((prev) => {
      const next = { ...prev };
      const currentActions = next[slug] || [];
      if (isChecked) {
        if (!currentActions.includes(action)) {
          next[slug] = [...currentActions, action];
        }
      } else {
        next[slug] = currentActions.filter((a) => a !== action);
      }
      return next;
    });
  };

  return (
    <Box sx={{ width: '100%' }}>
      {ResponseComponents}
      <Grid size={{ xs: 12, sm: 12, md: 12 }}>
        <Card
          sx={{
            alignItems: 'center',
            p: 3,
            borderRadius: 2,
            boxShadow: 3,
            bgcolor: 'background.paper',
            marginTop: paddingCardsTables,
          }}
        >
          <Box sx={{ width: '100%' }}>
            <Box>
              <Tabs
                TabIndicatorProps={{ style: { display: 'none' } }}
                selectionFollowsFocus={true}
                value={value}
                onChange={handleChangeApps}
                aria-label="tabs detalle clientes"
              >
                {customer.applications.map((app: any, index: any) => (
                  <CustomButtonTab
                    key={app.id}
                    icon={<Apps />}
                    iconPosition="start"
                    label={app.name}
                    {...a11yProps(index + 1)}
                    onClick={() => handleChangePermissions(index, app)}
                  />
                ))}
              </Tabs>
            </Box>
          </Box>

          <div>
            {permissionsTree && permissionsTree.length > 0 && (
              <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                <Button style={{ margin: 9 }} variant="contained" color="success" onClick={updatePermissions}>
                  Actualizar Permisos
                </Button>
              </div>
            )}

            {permissionsTree && permissionsTree.length > 0 ? (
              permissionsTree.map((item: any, index: any) => {
                const key = item.slug || item.permission_slug;
                const isModuleEnabled = !!selectedModules[key];
                
                return (
                  <React.Fragment key={index}>
                    {item.childrens && item.childrens.length > 0 && (
                      <Accordion sx={{ mt: 3, borderTop: 4, borderColor: '#192a56', borderRadius: 2, overflow: 'hidden', boxShadow: 3 }}>
                        <AccordionSummary
                          expandIcon={<ExpandMoreIcon sx={{ color: '#192a56' }} />}
                          sx={{ backgroundColor: '#f0f4f8' }}
                        >
                          <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                            <Typography component="div" variant="h6" sx={{ color: '#192a56', fontWeight: 'bold', display: 'flex', alignItems: 'center', gap: 1 }}>
                              {eachWordUpper(item.text)} <VerifiedIcon fontSize="small" />
                            </Typography>
                            <Typography variant="body2" component="div" sx={{ color: 'text.secondary' }}>
                              Sub módulos y permisos
                            </Typography>
                          </Box>
                        </AccordionSummary>
                        <AccordionDetails sx={{ backgroundColor: '#ffffff', padding: 2 }}>
                          <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                            
                            <Box sx={{ pl: 2, pb: 1 }}>
                              <FormGroup>
                                <FormControlLabel
                                  control={
                                    <Checkbox
                                      checked={!!topLevelChecks[key]}
                                      onChange={(e) => {
                                        setTopLevelChecks(prev => ({...prev, [key]: e.target.checked}));
                                        handleToggleModule(key, e.target.checked, []);
                                      }}
                                      name={`mod_${key}`}
                                    />
                                  }
                                  label="Habilitar Módulo"
                                />
                              </FormGroup>
                            </Box>

                            <div>
                              {item.childrens.map((element: any, indexf: any) => (
                                <RenderChildren
                                  key={indexf}
                                  element={element}
                                  parentSlugs={[key]}
                                  isParentEnabled={!!topLevelChecks[key]}
                                  selectedModules={selectedModules}
                                  selectedActions={selectedActions}
                                  handleToggleModule={handleToggleModule}
                                  handleToggleAction={handleToggleAction}
                                />
                              ))}
                            </div>
                          </Box>
                        </AccordionDetails>
                      </Accordion>
                    )}
                  </React.Fragment>
                );
              })
            ) : (
              <SinDatos subtitle="No hay informacion de modulos disponibles" />
            )}
          </div>
        </Card>
      </Grid>
    </Box>
  );
};
